import { forwardRef, memo } from "react";

import { useFormField } from "../../hooks/useFormField";
import type { Attribute, SharedFieldProps } from "../../types";

import { FieldContainer } from "./FieldContainer";
import { ReadOnlyField } from "./ReadOnlyField";

/**
 * 各フィールド入力コンポーネント（TextField, SelectFieldなど）に渡されるProps
 * FormFieldコンポーネントから直接渡されるプロパティ
 */
export interface BaseFieldProps extends SharedFieldProps {
  field: Attribute;
}

/**
 * 基本的なフィールドコンポーネント
 * 他のフィールドコンポーネントが拡張するためのベースとなります
 *
 * 主な責務:
 * 1. フィールドの周辺UI（ラベル、エラー表示など）を提供
 * 2. 入力コンポーネントをラップして統一的なUIを提供
 * 3. フィールド値の制御と変換を管理
 * 4. バリデーションを実行して結果を表示
 */
export const BaseField = memo(
  forwardRef<HTMLDivElement, BaseFieldProps>(function BaseField(
    {
      fieldId,
      field,
      readOnly,
      disabled,
      layout,
      className,
      "data-testid": testId,
      renderInput,
    },
    ref,
  ) {
    // useFormFieldフックを使用してフィールドの状態を管理
    const { value, error, handleChange, handleBlur, handleFocus } =
      useFormField({
        fieldId,
        attribute: field,
      });

    // 入力フォームをレンダリング
    return (
      <>
        {field.type === "Hidden" ? (
          <input type="hidden" />
        ) : (
          <FieldContainer
            id={fieldId}
            label={field?.label}
            error={error}
            helpText={field?.description}
            required={field?.required}
            readOnly={
              field.type !== "File" &&
              field.type !== "HighlyConfidentialText" &&
              readOnly
            } // FileフィールドとHighlyConfidentialTextフィールドは操作を可能にするため、Readonlyにしない
            fieldReadonly={field.type !== "File" && field.readonly}
            layout={layout || field?.layout || "vertical"}
            className={className}
            data-testid={testId}
            ref={ref}
          >
            <div className="field-control">
              {/* 読み取り専用で独自表示するフィールド */}
              {field.type !== "Boolean" &&
              field.type !== "ChangeBoolean" &&
              field.type !== "MultiSelectCheckbox" &&
              field.type !== "File" &&
              field.type !== "ChangeText" &&
              field.type !== "ChangeSingleSelect" &&
              field.type !== "HighlyConfidentialText" &&
              field.type !== "Person" &&
              field.type !== "Software" &&
              field.type !== "ChangeInteger" &&
              field.type !== "ChangeDecimal" &&
              field.type !== "ChangeRadio" &&
              (field.readonly || readOnly) ? (
                <ReadOnlyField value={value.currentValue} field={field} />
              ) : (
                renderInput?.({
                  id: fieldId,
                  name: fieldId,
                  value: value.currentValue,
                  previousValue: value.previousValue,
                  onChange: handleChange,
                  onFocus: handleFocus,
                  onBlur: handleBlur,
                  error,
                  placeholder: field?.placeholder,
                  disabled: disabled,
                  readOnly: readOnly || field.readonly,
                  required: field?.required,
                  "data-testid": testId,
                })
              )}
            </div>
          </FieldContainer>
        )}
      </>
    );
  }),
);

BaseField.displayName = "BaseField";
